/**
 * Build `tabSettings` payload for createTab() from a pricing scenario.
 *
 * Pure helper — no side effects. Keeps TestScenariosPage.jsx slim and unit-testable.
 *
 * @param {object} scenario - PRICING_SCENARIOS entry; uses scenario.calculatorConfig
 * @returns {object} tabSettings shaped like CalculatorContext expects
 */
const ALL_PRODUCT_IDS = [
  'business-phone',
  'call-tracking',
  'ai-conversation',
  'callback',
  'phone-numbers',
  'voice-traffic',
  'omnichannel-chat',
  'eniq-ai',
  'voicebot',
];

export function buildScenarioTabSettings(scenario) {
  const calculatorConfig = scenario?.calculatorConfig || {};
  const productIds = calculatorConfig.selectedProducts || [];
  const voiceTraffic = calculatorConfig.voiceTraffic || null;

  const hasFMC = productIds.includes('fmc');
  const hasBP = productIds.includes('business-phone');

  const selectedProducts = ALL_PRODUCT_IDS.map(id => ({
    id,
    active: id === 'business-phone'
      ? (hasBP || hasFMC)
      : productIds.includes(id),
  }));

  let businessPhoneType = 'business-phone';
  if (hasFMC && hasBP) businessPhoneType = 'business-phone-fmc';
  else if (hasFMC && !hasBP) businessPhoneType = 'fmc';

  const isCustomVoiceTraffic = voiceTraffic?.mode === 'custom';
  const hasVoiceTraffic = productIds.includes('voice-traffic');
  const phoneNumbers = Array.isArray(calculatorConfig.phoneNumbers)
    ? calculatorConfig.phoneNumbers
    : [];

  return {
    products: {
      businessPhoneUsers: calculatorConfig.businessPhoneUsers || 3,
      callTrackingSessions: calculatorConfig.ctSessions || 100,
      aiCalls: calculatorConfig.aiCalls || 100,
      callbackSessions: calculatorConfig.callbackSessions || 100,
      voiceTrafficMinutes: calculatorConfig.voiceTrafficMinutes || 1000,
      omnichannelChatUsers: calculatorConfig.chatUsers || 3,
      eniqAiMinutes: calculatorConfig.eniqMinutes || 2000,
      voicebotMinutes: calculatorConfig.vbMinutes || 1100,
      manualPackageSelection: false,
      selectedProducts,
    },
    packageTier: calculatorConfig.packageTier || 'basic',
    eniqTier: calculatorConfig.eniqTier || 'standard',
    fmcUsers: calculatorConfig.fmcUsers || 0,
    businessPhoneType,
    isCallbackEnabled: productIds.includes('callback'),
    phoneNumbers,
    enablePhoneActivation: Boolean(calculatorConfig.enablePhoneActivation),
    hasPhoneNumbersCustomSettings: phoneNumbers.length > 0,
    voiceTrafficCountriesMinutes: hasVoiceTraffic && !isCustomVoiceTraffic
      ? (voiceTraffic?.countriesMinutes || { UA: 500 })
      : {},
    voiceTrafficUseCustomAmount: isCustomVoiceTraffic,
    voiceTrafficCustomAmount: isCustomVoiceTraffic
      ? Number(voiceTraffic?.customAmount || 0)
      : 0,
    hasVoiceTrafficCustomSettings: hasVoiceTraffic,
  };
}
